import anylogger from '@app/anylogger'
import { useBrowserDocWin, useComponentHeight, useCurrentRef, useEvent, useMobileParams, useWindowSize } from '@app/hooks'
import { Flex, FlexC, IMenu, SplitFlex, useBreakpoint, useBreakpointValues, useCurrentBreakpoint, useMenu } from '@app/muiplus'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { LayoutOptions, LayoutServiceContext } from 'src/services/LayoutService'
import { SearchResultsBackdrop } from 'src/services/Search/SearchResultsBackdrop'
import { ColorModeContext } from './ColorModeContext'
import { MenuTree } from './MenuTree'
import { Sidebar } from './Sidebar'
import { TitleBar } from './TitleBar'
import BannerImageView from './BannerImageView'

const log = anylogger('GeneralLayout')

interface GeneralLayoutProps {
	bannerImage?: any
}
export const GeneralLayout = React.forwardRef(function GeneralLayout(props: PropsWithChildren<GeneralLayoutProps>, ref: any) {
	const { children, bannerImage, ...rest } = props

	const router = useRouter()
	const [showMenu, setShowMenu] = useState<boolean>(true)
	const [showTitleBar, setShowTitleBar] = useState<boolean>(true)
	const [backgroundOverride, setBackgroundOverride] = useState<string | undefined>(undefined)

	const [contentTop, setContentTop] = useState(0)

	const { mode, toggleColorMode } = React.useContext(ColorModeContext)
	const [contentRef, setContentRef] = useCurrentRef<HTMLElement>()
	const [contentScrollRef, setContentScrollRef] = useCurrentRef<HTMLElement>()
	const [containerRef, setContainerRef] = useCurrentRef<HTMLElement>()
	const [titleRef, setTitleRef] = useCurrentRef<HTMLDivElement>()
	const [menuRef, setMenuRef] = useCurrentRef<HTMLElement>()

	const [bannerHeight, setBannerHeight] = useState<number | undefined>(undefined)
	const [maxBannerHeight, setMaxBannerHeight] = useState<number>(0)
	const { browserWindow } = useBrowserDocWin()
	const menuVisible = browserWindow?.location.hash == '#menu' || false

	const isMob = useBreakpoint('mobile')
	const isTab = useBreakpoint('tablet')
	const isMobile = isMob || isTab
	const { items } = useMenu()

	let hideBannerOnScroll = isMobile

	const toggleMenu = useCallback(() => {
		if (menuVisible) router.back()
		else if (!menuVisible) router.push({ hash: '#menu' }, undefined, { shallow: true })
	}, [menuVisible, router])

	const onNavigate = useCallback(
		(menuItem: IMenu) => {
			if (!menuItem.target) return
			router.push(menuItem.target)
		},
		[router]
	)

	const onHome = () => {
		router.push('/Home')
	}
	const titleHeight = useComponentHeight(titleRef)
	const containerHeight = useComponentHeight(containerRef)
	if (containerHeight && maxBannerHeight == 0 && typeof bannerHeight == 'undefined' && bannerImage?.src) {
		const max = containerHeight / 3
		let maxHeight = Math.min(bannerImage.height || max, max)
		setMaxBannerHeight(maxHeight)
		setBannerHeight(maxHeight)
	}
	if (contentRef) {
		const r = contentRef.getBoundingClientRect()
		if (contentTop != r.top) setContentTop(r.top)
	}
	const contentHeight = containerHeight - titleHeight

	const findScrollElement = useCallback(
		(node: HTMLElement | null) => {
			if (!node) return undefined
			if (node == contentScrollRef || node == menuRef) return node
			return findScrollElement(node.parentElement)
		},
		[contentScrollRef, menuRef]
	)

	// this returns true if the header height needs to be adjusted
	const adjustHeaderHeight = useCallback(
		(delta: number, scrollElement?: HTMLElement) => {
			if (scrollElement) {
				if (delta > 0 && bannerHeight == 0) return false
				if (delta < 0 && scrollElement.scrollTop > 0) return false
			}

			setBannerHeight((prevHeight) => {
				if (hideBannerOnScroll) {
					if (delta > 0) return 0
					else return maxBannerHeight
				} else {
					const newHeight = Math.max(0, Math.min(maxBannerHeight, (prevHeight || 0) - delta))
					if (newHeight !== prevHeight) {
						return newHeight
					}
					return prevHeight
				}
			})
			return bannerHeight && bannerHeight > 0
		},
		[bannerHeight, hideBannerOnScroll, maxBannerHeight]
	)

	const onWheel = useCallback(
		(e: any) => {
			const scrollElement = findScrollElement(e.target)
			if (adjustHeaderHeight(e.deltaY, scrollElement)) {
				e.preventDefault()
				e.stopPropagation()
			}
		},
		[adjustHeaderHeight, findScrollElement]
	)
	const onKeyDown = useCallback(
		(e: any) => {
			if (e.key == 'ArrowDown' || e.key == 'ArrowUp') {
				// this event always comes from the body, so we can't call findScrollElement to see if we are "in" the scroll content area
				if (adjustHeaderHeight(e.key == 'ArrowUp' ? -40 : 40, contentScrollRef || undefined)) {
					e.preventDefault()
					e.stopPropagation()
				}
			}
		},
		[adjustHeaderHeight, contentScrollRef]
	)
	const touchPos = useRef<number>(-1)
	const bannerWasHidden = useRef(false)

	const onTouchStart = useCallback(
		(e: any) => {
			if (e.changedTouches.length) {
				touchPos.current = e.changedTouches[0].clientY
				if (bannerHeight == 0 && maxBannerHeight > 0) bannerWasHidden.current = true
				else bannerWasHidden.current = false
			}
		},
		[bannerHeight, maxBannerHeight]
	)
	const onTouchEnd = useCallback((e: any) => {
		touchPos.current = -1
	}, [])
	const { addressBarVisible, addressBarHeight, isPortrait } = useMobileParams()

	const onTouchMove = useCallback(
		(e: any) => {
			if (touchPos.current == -1) return
			if (e.touches.length > 1) return

			const delta = touchPos.current - e.changedTouches[0].clientY

			// this doesn't work, probably because we cannot the top level element does not scroll.
			// if (delta > 0 && document.documentElement.clientHeight == window.innerHeight) {
			// 	document.documentElement.scrollTo(0, 1)
			// 	e.preventDefault()
			// 	return
			// }

			touchPos.current = e.changedTouches[0].clientY

			if (delta > 0 && bannerHeight == 0 && maxBannerHeight > 0) return false
			if (delta < 0 && contentScrollRef && contentScrollRef.scrollTop > 0) return false
			if (delta > 0) setBannerHeight(0)
			else setBannerHeight(maxBannerHeight)

			if ((bannerWasHidden.current && delta < 0) || ((bannerHeight || 0) > 0 && contentScrollRef && contentScrollRef.scrollTop > 0)) {
				e.preventDefault()
				e.stopPropagation()
			}
		},
		[bannerHeight, contentScrollRef, maxBannerHeight]
	)
	// log('size', document.documentElement.clientHeight, window.innerHeight)

	useEvent(containerRef, 'onWheel', onWheel, { passive: false })
	useEvent(containerRef, 'touchStart', onTouchStart, { passive: true })
	useEvent(containerRef, 'touchEnd', onTouchEnd, { passive: true })
	useEvent(containerRef, 'touchMove', onTouchMove, { passive: false })
	useEvent(window, 'keydown', onKeyDown, { passive: false })

	// we want to show the menu on mobile, even if showMenu was turned off by descendants
	const mobileMenu = isMobile && (
		<Sidebar top={titleHeight} items={items} onNavigate={onNavigate} open={menuVisible} onClose={toggleMenu} />
	)

	const getContent = useCallback(() => {
		if (isMobile)
			return (
				<ChildScroller ref={setContentScrollRef} preventScroll={addressBarVisible}>
					{children}
				</ChildScroller>
			)
		return (
			<SplitFlex gap="0px" initialSize="content" localStorageKey="Menu.Width" hidePrimary={!showMenu}>
				<Flex fill>
					<MenuTree ref={setMenuRef} items={items} onNavigate={onNavigate} localStorageKey="Menu.Position" />
				</Flex>
				<Flex fill pt="0px" pl="0px">
					<ChildScroller ref={setContentScrollRef}>{children}</ChildScroller>
				</Flex>
			</SplitFlex>
		)
	}, [addressBarVisible, children, isMobile, items, onNavigate, setContentScrollRef, setMenuRef, showMenu])

	const [service] = useState<LayoutOptions>({
		showMenu: setShowMenu,
		showTitleBar: setShowTitleBar,
		backgroundOverride: setBackgroundOverride
	})
	const size = useWindowSize()
	const breakpoint = useCurrentBreakpoint()
	useEffect(() => {
		if (breakpoint) log('screen size:', breakpoint, size.width, size.height)
	}, [breakpoint, size.height, size.width])

	const padding = showTitleBar || isMobile ? 1 : 0

	return (
		<LayoutServiceContext.Provider value={service}>
			{/* overflow=hidden allows the SessionStatusdrop down indicator outside of its parameters without
							causing a horizontal scroll bar on the whole */}
			<FlexC ref={setContainerRef} gap="0px" fill {...rest} bgcolor={backgroundOverride} overflow="hidden">
				<div
					ref={setTitleRef}
					style={{
						display: showTitleBar || isMobile ? undefined : 'none',
						padding: '0.5em'
					}}
				>
					<TitleBar onMenuToggle={toggleMenu} onHome={onHome} isMobile={isMobile} />
				</div>
				<BannerImageView
					bannerImage={bannerImage}
					height={bannerHeight}
					transitionParams={hideBannerOnScroll ? '0.5s linear' : '0.2s linear'}
				/>
				<Flex
					ref={setContentRef}
					fill
					sx={{
						position: 'relative'
					}}
				>
					{mobileMenu}
					{getContent()}
					{!isMobile && <SearchResultsBackdrop anchorRef={titleRef} height={contentHeight} />}
				</Flex>
			</FlexC>
		</LayoutServiceContext.Provider>
	)
})
interface ChildScrollerProps {
	preventScroll?: boolean
}
const ChildScroller = React.forwardRef(function ChildScroller(props: PropsWithChildren<ChildScrollerProps>, passedRef: any) {
	const { preventScroll = false } = props
	const [ref, setRef] = useCurrentRef<HTMLElement>(passedRef)
	return (
		<Flex ref={setRef} fill overflow={preventScroll ? 'hidden' : 'auto'}>
			{props.children}
		</Flex>
	)
})
